//C:\Users\Anish\xpr_survey\XPRSuvey-App\src\Components\Survey\Pages\Reports.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useLocation } from "react-router-dom";
import {
    Download,
    CheckCircle2,
    ChevronsLeft,
    ChevronLeft,
    ChevronRight,
    ChevronsRight,
    RotateCcw,
    Clock,
    TrendingUp,
    Search,
    Columns3,
    AlignJustify
} from "lucide-react";
import {
    GetAssessmentReportDetails as getSurveyAssessmentReportDetails,
    GetExcelReport as getSurveyExcelReport,
    GetBulkExcelReport as getSurveyBulkExcelReport,
} from "../Services/SurveyService";

const STATUS_STYLES = {
    completed: "bg-[#c2a8f033] text-[#7f35b2]",
    pending: "bg-amber-50 text-amber-600",
    "in progress": "bg-blue-50 text-blue-600",
    "not started": "bg-gray-100 text-gray-500",
};

const GRADE_BADGE_STYLES = {
    a: "bg-[#c2a8f033] text-[#7f35b2]",
    b: "bg-[#c2a8f033] text-[#7f35b2]",
    c: "bg-amber-50 text-amber-600",
    d: "bg-rose-50 text-rose-600",
    f: "bg-rose-50 text-rose-600",
};

const PAGE_SIZE_OPTIONS = [10, 25, 50];

// 1. REMOVED INVITED BY FIELDS FROM OPTIONAL_COLUMNS
const OPTIONAL_COLUMNS = [
    { key: "score", label: "Score/Question" },
    { key: "submission", label: "Submission" },
    { key: "grade", label: "Section A grade" },
    { key: "band", label: "Section B band" },
    { key: "assigned", label: "Assigned" },
    { key: "completed", label: "Completed" },
    { key: "deadline", label: "Deadline" },
    { key: "gateKeeperCount", label: "Gatekeeper Count" },
    { key: "redFlagCount", label: "Red Flag Count" },
    { key: "sectionAScore", label: "Section A Score" },
    { key: "survivalScore", label: "Survival Score" },
    { key: "productivityScore", label: "Productivity Score" },
    { key: "salesReadinessScore", label: "Sales Readiness Score" },
    { key: "overallAssessmentScore", label: "Overall Assessment Score" },
    { key: "isEliminated", label: "Is Eliminated" },
];

const EMPTY_FILTERS = { search: "", from: "", to: "", assessment: "", status: "", channel: "", experience: "" };

const isRowSelectable = (row) => row.candidateStatus?.toLowerCase() !== "not started";

const parseApiDate = (dateStr) => {
    if (!dateStr) return null;
    const [datePart, timePart] = dateStr.split(" ");
    const [day, month, year] = datePart.split("-").map(Number);
    const [hh = 0, mm = 0, ss = 0] = (timePart ?? "").split(":").map(Number);
    return new Date(year, month - 1, day, hh, mm, ss);
};

const formatDisplayDate = (date) => {
    if (!date) return "—";
    return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    });
};

const getInitials = (name) => {
    if (!name) return "??";
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const getGradeBadgeClass = (grade) =>
    GRADE_BADGE_STYLES[grade?.toString().trim().toLowerCase()] ?? "bg-gray-100 text-gray-500";

const getBandDotClass = (band) => {
    const b = (band || "").toLowerCase();
    if (b.includes("not recommend")) return "bg-rose-500";
    if (b.includes("caution")) return "bg-amber-500";
    if (b.includes("recommend")) return "bg-[#7f35b2]";
    return "bg-gray-300";
};

const StatusBadge = ({ status }) => {
    const styleKey = status?.toLowerCase() ?? "";
    const isCompleted = styleKey === "completed";

    return (
        <span
            className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-[62%] font-medium ${STATUS_STYLES[styleKey] ?? "bg-gray-100 text-gray-600"
                }`}
        >
            {isCompleted ? <CheckCircle2 size={13} /> : <Clock size={13} />}
            {status}
        </span>
    );
};

const StatCard = ({ icon, label, value }) => (
    <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-md">
        <div className="flex items-center gap-2 text-xs font-medium text-gray-500">
            {icon}
            {label}
        </div>
        <div className="mt-1.5 text-2xl font-bold text-gray-900">{value}</div>
    </div>
);

function Reports({
    services = {
        getAssessmentReportDetails: getSurveyAssessmentReportDetails,
        getExcelReport: getSurveyExcelReport,
        getBulkExcelReport: getSurveyBulkExcelReport,
    },
}) {
    const location = useLocation();
    const [rawData, setRawData] = useState([]);
    const [loading, setLoading] = useState(true);

    const [filters, setFilters] = useState(() => ({
        ...EMPTY_FILTERS,
        ...(location.state?.initialFilters || {}),
    }));

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);

    const [downloadingRows, setDownloadingRows] = useState(new Set());
    const [selectedRows, setSelectedRows] = useState(new Set());
    const [isBulkDownloading, setIsBulkDownloading] = useState(false);

    const [visibleColumns, setVisibleColumns] = useState(() =>
        OPTIONAL_COLUMNS.reduce((acc, col) => ({ ...acc, [col.key]: false }), {})
    );
    const [columnsMenuOpen, setColumnsMenuOpen] = useState(false);
    const [compactRows, setCompactRows] = useState(false);
    const columnsMenuRef = useRef(null);

    const [openRowMenu, setOpenRowMenu] = useState(null);
    const [expandedMobileRow, setExpandedMobileRow] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const fetchReportData = async () => {
            setLoading(true);
            try {
                const res = await services.getAssessmentReportDetails();
                if (!cancelled && res?.data?.status === "success") {
                    setRawData(res?.data?.surveyDetails ?? []);
                }
            } catch (error) {
                CustomToast("fail", "Something went wrong please try again later.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        fetchReportData();
        return () => { cancelled = true; };
    }, [])

    useEffect(() => {
        if (!columnsMenuOpen) return;
        const handleClick = (e) => {
            if (columnsMenuRef.current && !columnsMenuRef.current.contains(e.target)) {
                setColumnsMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [columnsMenuOpen]);

    useEffect(() => {
        if (!openRowMenu) return;
        const handleClick = (e) => {
            if (!e.target.closest('[data-row-menu]')) setOpenRowMenu(null);
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [openRowMenu]);

    useEffect(() => {
        setCurrentPage(1);
    }, [filters]);

    const assessmentOptions = useMemo(
        () => [...new Set(rawData.map((r) => r.assessmentName))],
        [rawData]
    );

    const statusOptions = useMemo(
        () => [...new Set(rawData.map((r) => r.candidateStatus))],
        [rawData]
    );

    const channelOptions = useMemo(
        () => [...new Set(rawData.map((r) => r.channelName).filter(Boolean))],
        [rawData]
    );

    const experienceOptions = useMemo(
        () => [...new Set(rawData.map((r) => r.experienceType).filter(Boolean))],
        [rawData]
    );

    const handleFilterChange = (key) => (e) => setFilters((prev) => ({ ...prev, [key]: e.target.value }));

    const handleReset = () => {
        setFilters(EMPTY_FILTERS);
        setCurrentPage(1);
        setSelectedRows(new Set());
    };

    const parseFilterDate = (dateStr) => {
        if (!dateStr) return null;
        const [year, month, day] = dateStr.split("-").map(Number);
        return new Date(year, month - 1, day);
    };

    const filteredData = useMemo(() => {
        const fromDate = parseFilterDate(filters.from);
        const toDate = parseFilterDate(filters.to);
        if (toDate) toDate.setHours(23, 59, 59, 999);
        const search = filters.search.trim().toLowerCase();

        return rawData.filter((row) => {
            if (!row.responderUID) return false;
            const targetDate = parseApiDate(row.completedTime);
            if (fromDate && targetDate && targetDate < fromDate) return false;
            if (toDate && targetDate && targetDate > toDate) return false;
            if ((fromDate || toDate) && !targetDate) return false;
            if (filters.assessment && row.assessmentName !== filters.assessment) return false;
            if (filters.status && row.candidateStatus !== filters.status) return false;
            if (filters.channel && row.channelName !== filters.channel) return false;
            if (filters.experience && row.experienceType !== filters.experience) return false;
            if (search) {
                const haystack = `${row.candidateName ?? ""} ${row.candidateEmail ?? ""}`.toLowerCase();
                if (!haystack.includes(search)) return false;
            }

            return true;
        });
    }, [rawData, filters]);

    const stats = useMemo(() => {
        const total = filteredData.length;
        const completedRows = filteredData.filter((r) => r.candidateStatus?.toLowerCase() === "completed");
        const completed = completedRows.length;
        const awaiting = total - completed;
        const avgScore = completedRows.length
            ? Math.round(completedRows.reduce((sum, r) => sum + (Number(r.totalScore) || 0), 0) / completedRows.length)
            : 0;
        return { total, completed, awaiting, avgScore };
    }, [filteredData]);

    const totalPages = Math.max(1, Math.ceil(filteredData.length / pageSize));
    const pagedData = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredData.slice(start, start + pageSize);
    }, [filteredData, currentPage, pageSize]);

    const selectablePagedRows = useMemo(() => pagedData.filter(isRowSelectable), [pagedData]);

    const handleRowDownload = async (row) => {
        if (downloadingRows.has(row.responderUID)) return;

        setDownloadingRows((prev) => new Set(prev).add(row.responderUID));

        try {
            const res = await services.getExcelReport(row.assessmentID, row.responderUID);

            if (res.data.type === "application/json") {
                const text = await res.data.text();
                const parsed = JSON.parse(text);
                CustomToast("fail", parsed.message || "Failed to generate report.");
                return;
            }

            const blob = new Blob([res.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.download = `${row.assessmentName}_${row.candidateName || row.responderUID}.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            if (error.status === 404) {
                CustomToast("fail", "The assessment could not be found, has not started, or has expired.");
            } else {
                CustomToast("fail", "Something went wrong while downloading the report.");
            }
        } finally {
            setDownloadingRows((prev) => {
                const next = new Set(prev);
                next.delete(row.responderUID);
                return next;
            });
        }
    };

    const handleBulkDownload = async () => {
        if (selectedRows.size === 0 || isBulkDownloading) return;
        setIsBulkDownloading(true);
        try {
            const responderKeys = rawData
                .filter((row) => selectedRows.has(row.responderUID))
                .map((row) => ({ AssessmentID: row.assessmentID, ResponderUID: row.responderUID }));

            const res = await services.getBulkExcelReport(responderKeys);

            const blob = new Blob([res.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `bulk_report_${Date.now()}.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            CustomToast("fail", "Something went wrong while downloading the bulk report.");
        } finally {
            setIsBulkDownloading(false);
        }
    };

    const rangeStart = filteredData.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const rangeEnd = Math.min(currentPage * pageSize, filteredData.length);

    const toggleRowSelection = (row) => {
        if (!isRowSelectable(row)) return;
        setSelectedRows((prev) => {
            const next = new Set(prev);
            next.has(row.responderUID) ? next.delete(row.responderUID) : next.add(row.responderUID);
            return next;
        });
    };

    const isAllPageSelected =
        selectablePagedRows.length > 0 && selectablePagedRows.every((row) => selectedRows.has(row.responderUID));

    const toggleSelectAllOnPage = () => {
        setSelectedRows((prev) => {
            const next = new Set(prev);
            selectablePagedRows.forEach((row) =>
                isAllPageSelected ? next.delete(row.responderUID) : next.add(row.responderUID)
            );
            return next;
        });
    };

    const toggleColumn = (key) => setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }));
    const toggleMobileDetails = (uid) => setExpandedMobileRow((prev) => (prev === uid ? null : uid));

    const inputClass =
        "rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 " +
        "focus:border-[#7f35b2] focus:outline-none focus:ring-1 focus:ring-[#7f35b2] max-md:w-full";
    // 2. UPDATED BASE COLUMN COUNT TO 6
    const visibleColumnCount = 7 + OPTIONAL_COLUMNS.filter((c) => visibleColumns[c.key]).length;
    const rowPaddingClass = compactRows ? "py-1.5" : "py-2";

    return (
        <div className="space-y-4 sm:space-y-6">
            <div className="flex gap-3 flex-row sm:items-start sm:justify-between">
                <div>
                    <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">Reports & View</h1>
                    <p className="mt-1 text-xs md:text-sm text-gray-500">
                        Each assignment is shown as a separate attempt, even for the same candidate.
                    </p>
                </div>
                <div className="flex items-start shrink-0 md:items-center gap-2">
                    <div className="relative" ref={columnsMenuRef}>
                        <button
                            type="button"
                            onClick={() => setColumnsMenuOpen((v) => !v)}
                            className="flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-600 shadow-sm hover:bg-gray-50"
                        >
                            <Columns3 size={16} />
                            <span className="hidden sm:inline">Columns</span>
                        </button>
                        {columnsMenuOpen && (
                            <div className="absolute right-0 z-20 mt-2 w-52 rounded-xl border border-gray-100 bg-white p-2 shadow-lg">
                                {OPTIONAL_COLUMNS.map((col) => (
                                    <label
                                        key={col.key}
                                        className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={visibleColumns[col.key]}
                                            onChange={() => toggleColumn(col.key)}
                                            className="h-3.5 w-3.5 rounded border-gray-300 text-[#7f35b2] focus:ring-[#7f35b2]"
                                        />
                                        {col.label}
                                    </label>
                                ))}
                                <hr className="my-1 border-gray-100" />
                                <button
                                    type="button"
                                    onClick={() => setCompactRows(!compactRows)}
                                    className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                                >
                                    <AlignJustify size={16} className="text-gray-500" />
                                    Compact rows
                                </button>
                            </div>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={handleBulkDownload}
                        disabled={selectedRows.size === 0 || isBulkDownloading}
                        className="flex items-center gap-2 rounded-xl bg-[#7f35b2] px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[#3bb290] disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400 sm:px-4"
                    >
                        <Download size={16} />
                        <span className="hidden sm:inline">
                            {isBulkDownloading ? "Downloading…" : `Bulk Download${selectedRows.size ? ` (${selectedRows.size})` : ""}`}
                        </span>
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <StatCard icon={<TrendingUp size={14} className="text-[#7f35b2]" />} label="Total attempts" value={stats.total} />
                <StatCard icon={<CheckCircle2 size={14} className="text-[#7f35b2]" />} label="Completed" value={stats.completed} />
                <StatCard icon={<Clock size={14} className="text-[#7f35b2]" />} label="Awaiting" value={stats.awaiting} />
                <StatCard icon={<TrendingUp size={14} className="text-[#7f35b2]" />} label="Avg score" value={stats.avgScore} />
            </div>

            <div className="rounded-2xl border border-gray-100 bg-white p-3 shadow-md">
                <div className="flex flex-wrap items-center gap-2">
                    <div className="relative min-w-0 flex-1 sm:max-w-xs">
                        <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            value={filters.search}
                            onChange={handleFilterChange("search")}
                            placeholder="Search candidate or email"
                            className={`w-full py-1.5 pl-9 pr-3 ${inputClass}`}
                        />
                    </div>
                    <select value={filters.assessment} onChange={handleFilterChange("assessment")} className={inputClass}>
                        <option value="">All assessments</option>
                        {assessmentOptions.map((name) => (
                            <option key={name} value={name}>{name}</option>
                        ))}
                    </select>
                    <select value={filters.status} onChange={handleFilterChange("status")} className={inputClass}>
                        <option value="">All statuses</option>
                        {statusOptions.map((status) => (
                            <option key={status} value={status}>{status}</option>
                        ))}
                    </select>
                    <select value={filters.channel} onChange={handleFilterChange("channel")} className={inputClass}>
                        <option value="">All channels</option>
                        {channelOptions.map((channel) => (
                            <option key={channel} value={channel}>{channel}</option>
                        ))}
                    </select>
                    <select value={filters.experience} onChange={handleFilterChange("experience")} className={inputClass}>
                        <option value="">All experience types</option>
                        {experienceOptions.map((exp) => (
                            <option key={exp} value={exp}>{exp}</option>
                        ))}
                    </select>
                    <input type="date" value={filters.from} onChange={handleFilterChange("from")} className={inputClass} />
                    <input type="date" value={filters.to} onChange={handleFilterChange("to")} className={inputClass} />
                    <button
                        type="button"
                        onClick={handleReset}
                        className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700"
                    >
                        <RotateCcw size={14} />
                        Reset
                    </button>
                </div>
            </div>

            <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-md">
                {/* Mobile card list (<768px) */}
                <div className="divide-y divide-gray-100 md:hidden">
                    {loading ? (
                        <div className="px-6 py-10 text-center text-gray-400">Loading report data…</div>
                    ) : pagedData.length === 0 ? (
                        <div className="px-6 py-10 text-center text-gray-400">No attempts match the selected filters.</div>
                    ) : (
                        pagedData.map((row) => {
                            const isNotStarted = !isRowSelectable(row) || row.candidateStatus?.toLowerCase() === "pending";
                            const hasResponder = row.candidateName !== "" && row.candidateEmail !== "";
                            const selectable = isRowSelectable(row);

                            return (
                                <div key={row.responderUID} className="space-y-3 p-4">
                                    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3">
                                        <div className="flex min-w-0 items-start gap-3">
                                            <input
                                                type="checkbox"
                                                checked={selectedRows.has(row.responderUID)}
                                                disabled={!selectable}
                                                onChange={() => toggleRowSelection(row)}
                                                title={!selectable ? "Not started — nothing to download yet" : undefined}
                                                className="mt-2 h-4 w-4 shrink-0 rounded border-gray-300 text-[#7f35b2] focus:ring-[#7f35b2] disabled:cursor-not-allowed disabled:opacity-40"
                                            />
                                            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#c2a8f033] text-sm font-bold text-[#7f35b2]">
                                                {getInitials(row.candidateName)}
                                            </div>
                                            <div className="min-w-0">
                                                {hasResponder ? (
                                                    <>
                                                        <div className="truncate font-semibold text-gray-900">{row.candidateName}</div>
                                                        <div className="truncate text-xs text-gray-400">{row.candidateEmail}</div>
                                                    </>
                                                ) : (
                                                    <div className="text-sm font-semibold text-amber-500">No Responder Yet</div>
                                                )}
                                                <div className="truncate text-xs text-gray-400">{row.assessmentName}</div>
                                            </div>
                                        </div>
                                        <StatusBadge status={row.candidateStatus} />
                                    </div>

                                    <div className="flex items-center justify-between gap-3 rounded-xl bg-gray-50 px-3 py-2">
                                        {visibleColumns.score ? (
                                            <div className="text-sm font-semibold text-gray-900">
                                                {row.totalScore ?? 0}
                                                <span className="text-gray-400"> / {row.totalQuestion ?? 0}</span>
                                            </div>
                                        ) : (
                                            <div />
                                        )}
                                        <div className="flex min-w-0 items-center justify-end w-40 text-sm font-medium text-gray-800 truncate">
                                            {row.finalRecommendation || "—"}
                                        </div>
                                    </div>

                                    {expandedMobileRow === row.responderUID && (
                                        <div className="grid grid-cols-2 gap-x-3 gap-y-2 rounded-xl border border-gray-100 px-3 py-2 text-xs text-gray-500">
                                            {visibleColumns.score && <div><span className="text-gray-400">Score/Question: </span>{row.totalScore ?? 0} / {row.totalQuestion ?? 0}</div>}
                                            <div><span className="text-gray-400">Experience: </span>{row.experienceType || "—"}</div>
                                            <div><span className="text-gray-400">Channel: </span>{row.channelName || "—"}</div>
                                            {visibleColumns.submission && <div><span className="text-gray-400">Submission: </span>{row.isAutoSubmitted ? "Auto-submitted" : "Manual"}</div>}
                                            {visibleColumns.grade && <div><span className="text-gray-400">Result: </span>{row.sectionAGradeTitle || "—"}</div>}
                                            {visibleColumns.assigned && <div><span className="text-gray-400">Assigned: </span>{formatDisplayDate(parseApiDate(row.assignedAt))}</div>}
                                            {visibleColumns.completed && <div><span className="text-gray-400">Completed: </span>{formatDisplayDate(parseApiDate(row.completedTime))}</div>}
                                            {visibleColumns.deadline && <div><span className="text-gray-400">Deadline: </span>{formatDisplayDate(parseApiDate(row.deadline))}</div>}
                                            <div><span className="text-gray-400">Invited By: </span>{row.invitedByUsername || "—"}</div>
                                            <div><span className="text-gray-400">Company: </span>{row.invitedByCompanyName || "—"}</div>
                                            {visibleColumns.gateKeeperCount && <div><span className="text-gray-400">Gatekeeper: </span>{row.gateKeeperCount ?? "—"}</div>}
                                            {visibleColumns.redFlagCount && <div><span className="text-gray-400">Red Flag: </span>{row.redFlagCount ?? "—"}</div>}
                                            {visibleColumns.sectionAScore && <div><span className="text-gray-400">Section A Score: </span>{row.sectionAScore ?? "—"}</div>}
                                            {visibleColumns.survivalScore && <div><span className="text-gray-400">Survival Score: </span>{row.survivalScore ?? "—"}</div>}
                                            {visibleColumns.productivityScore && <div><span className="text-gray-400">Productivity: </span>{row.productivityScore ?? "—"}</div>}
                                            {visibleColumns.salesReadinessScore && <div><span className="text-gray-400">Sales Readiness: </span>{row.salesReadinessScore ?? "—"}</div>}
                                            {visibleColumns.overallAssessmentScore && <div><span className="text-gray-400">Overall Score: </span>{row.overallAssessmentScore != null ? Number(row.overallAssessmentScore).toFixed(2) : "—"}</div>}
                                            {visibleColumns.isEliminated && <div><span className="text-gray-400">Eliminated: </span>{row.isEliminated != null ? (row.isEliminated ? "Yes" : "No") : "—"}</div>}
                                        </div>
                                    )}

                                    <div className="flex items-center justify-between pt-1">
                                        <button
                                            type="button"
                                            onClick={() => toggleMobileDetails(row.responderUID)}
                                            className="text-xs font-medium text-[#7f35b2]"
                                        >
                                            {expandedMobileRow === row.responderUID ? "Hide details" : "View details"}
                                        </button>
                                        <button
                                            type="button"
                                            disabled={isNotStarted || downloadingRows.has(row.responderUID)}
                                            onClick={() => handleRowDownload(row)}
                                            className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${isNotStarted || downloadingRows.has(row.responderUID)
                                                ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
                                                : "border-[#7f35b2]/40 text-[#7f35b2] bg-white hover:bg-[#c2a8f033]"
                                                }`}
                                            title={isNotStarted ? "Download unavailable for not started assessments" : "Download Report"}
                                        >
                                            <Download size={13} />
                                            {downloadingRows.has(row.responderUID) ? "Preparing…" : "Report"}
                                        </button>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Table (>=768px) */}
                <div className="hidden overflow-x-auto md:block">
                    <table className="w-full text-left text-sm">
                        <thead className="sticky top-0 z-10 bg-gray-50 text-[11px] uppercase tracking-wider text-gray-400">
                            <tr className="border-b border-gray-100">
                                <th className="sticky left-0 z-10 w-12 whitespace-nowrap bg-gray-50 px-4 py-3">
                                    <input
                                        type="checkbox"
                                        checked={isAllPageSelected}
                                        onChange={toggleSelectAllOnPage}
                                        disabled={selectablePagedRows.length === 0}
                                        title={selectablePagedRows.length === 0 ? "No selectable attempts on this page" : undefined}
                                        className="h-4 w-4 rounded border-gray-300 text-[#7f35b2] focus:ring-[#7f35b2] disabled:cursor-not-allowed disabled:opacity-40"
                                    />
                                </th>
                                <th className="sticky left-12 z-10 bg-gray-50 px-2 py-3 font-semibold">Candidate</th>
                                <th className="whitespace-nowrap px-4 py-3 font-semibold">Status</th>
                                {visibleColumns.score && <th className="whitespace-nowrap px-4 py-3 font-semibold">Score/Question</th>}
                                <th className="whitespace-nowrap px-4 py-3 font-semibold">Final Recommendation</th>
                                {/* 3. ADDED DEFAULT HEADER FOR INVITED BY */}
                                <th className="whitespace-nowrap px-4 py-3 font-semibold">Invited By</th>
                                <th className="whitespace-nowrap px-4 py-3 font-semibold">Experience & Channel</th>
                                {visibleColumns.submission && <th className="whitespace-nowrap px-4 py-3 font-semibold">Submission</th>}
                                {visibleColumns.grade && <th className="whitespace-nowrap px-4 py-3 font-semibold">Section A Grade</th>}
                                {visibleColumns.band && <th className="whitespace-nowrap px-4 py-3 font-semibold">Section B Band</th>}
                                {visibleColumns.assigned && <th className="whitespace-nowrap px-4 py-3 font-semibold">Assigned</th>}
                                {visibleColumns.completed && <th className="whitespace-nowrap px-4 py-3 font-semibold">Completed</th>}
                                {visibleColumns.deadline && <th className="whitespace-nowrap px-4 py-3 font-semibold">Deadline</th>}
                                {visibleColumns.gateKeeperCount && <th className="whitespace-nowrap px-4 py-3 font-semibold">Gatekeeper Count</th>}
                                {visibleColumns.redFlagCount && <th className="whitespace-nowrap px-4 py-3 font-semibold">Red Flag Count</th>}
                                {visibleColumns.sectionAScore && <th className="whitespace-nowrap px-4 py-3 font-semibold">Section A Score</th>}
                                {visibleColumns.survivalScore && <th className="whitespace-nowrap px-4 py-3 font-semibold">Survival Score</th>}
                                {visibleColumns.productivityScore && <th className="whitespace-nowrap px-4 py-3 font-semibold">Productivity Score</th>}
                                {visibleColumns.salesReadinessScore && <th className="whitespace-nowrap px-4 py-3 font-semibold">Sales Readiness Score</th>}
                                {visibleColumns.overallAssessmentScore && <th className="whitespace-nowrap px-4 py-3 font-semibold">Overall Assessment Score</th>}
                                {visibleColumns.isEliminated && <th className="whitespace-nowrap px-4 py-3 font-semibold">Is Eliminated</th>}
                                <th className="whitespace-nowrap px-4 py-3 text-right font-semibold">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={visibleColumnCount} className="px-6 py-10 text-center text-gray-400">
                                        Loading report data…
                                    </td>
                                </tr>
                            ) : pagedData.length === 0 ? (
                                <tr>
                                    <td colSpan={visibleColumnCount} className="px-6 py-10 text-center text-gray-400">
                                        No attempts match the selected filters.
                                    </td>
                                </tr>
                            ) : (
                                pagedData.map((row) => {
                                    const isNotStarted = row.candidateStatus?.toLowerCase() === "not started" || row.candidateStatus?.toLowerCase() === "pending";
                                    const hasResponder = row.candidateName !== "" && row.candidateEmail !== "";
                                    const selectable = isRowSelectable(row);

                                    return (
                                        <tr key={row.responderUID} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/60">
                                            <td className={`sticky left-0 z-[1] w-12 bg-white px-4 ${rowPaddingClass}`}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedRows.has(row.responderUID)}
                                                    disabled={!selectable}
                                                    onChange={() => toggleRowSelection(row)}
                                                    title={!selectable ? "Not started — nothing to download yet" : undefined}
                                                    className="h-4 w-4 rounded border-gray-300 text-[#7f35b2] focus:ring-[#7f35b2] disabled:cursor-not-allowed disabled:opacity-40"
                                                />
                                            </td>
                                            <td className={`sticky left-12 z-[1] bg-white px-2 ${rowPaddingClass}`}>
                                                <div className="flex min-w-0 items-center gap-3">
                                                    <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#c2a8f033] text-xs font-bold text-[#7f35b2]">
                                                        {getInitials(row.candidateName)}
                                                    </div>
                                                    <div className="min-w-0">
                                                        {hasResponder ? (
                                                            <div className="truncate font-semibold text-gray-900">{row.candidateName}</div>
                                                        ) : (
                                                            <div className="text-amber-500">No Responder Yet</div>
                                                        )}
                                                        <div className="truncate text-xs text-gray-400">
                                                            {hasResponder ? `${row.candidateEmail} · ` : ""}{row.assessmentName}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className={`px-4 ${rowPaddingClass}`}>
                                                <div className="flex flex-wrap items-center gap-1.5">
                                                    <StatusBadge status={row.candidateStatus} />
                                                </div>
                                            </td>
                                            {visibleColumns.score && (
                                                <td className={`px-4 ${rowPaddingClass}`}>
                                                    <div className="font-semibold text-gray-800">
                                                        {row.totalScore ?? 0}
                                                        <span className="text-gray-400"> / {row.totalQuestion ?? 0}</span>
                                                    </div>
                                                </td>
                                            )}
                                            <td className={`px-4 ${rowPaddingClass}`}>
                                                <div className="font-small text-gray-800">
                                                    {row.finalRecommendation || "—"}
                                                </div>
                                            </td>
                                            {/* 4. ADDED STACKED DEFAULT CELL FOR INVITED BY USER & COMPANY */}
                                            <td className={`px-4 ${rowPaddingClass}`}>
                                                <div className="min-w-0">
                                                    <div className="truncate font-medium text-gray-800">
                                                        {row.invitedByUsername || "—"}
                                                    </div>
                                                    <div className="truncate text-xs text-gray-400">
                                                        {row.invitedByCompanyName || "—"}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className={`px-4 ${rowPaddingClass}`}>
                                                <div className="min-w-0">
                                                    <div className="truncate font-medium text-gray-800">
                                                        {row.experienceType || "—"}
                                                    </div>
                                                    <div className="truncate text-xs text-gray-400">
                                                        {row.channelName || "—"}
                                                    </div>
                                                </div>
                                            </td>
                                            {visibleColumns.submission && (
                                                <td className={`whitespace-nowrap px-4 text-xs text-gray-400 ${rowPaddingClass}`}>
                                                    {row.isAutoSubmitted ? "Auto-submitted" : "Manual"}
                                                </td>
                                            )}
                                            {visibleColumns.grade && (
                                                <td className={`px-4 ${rowPaddingClass}`}>
                                                    {row.sectionAGrade ? (
                                                        <span className={`inline-flex h-6 w-6 items-center justify-center rounded-md text-xs font-bold ${getGradeBadgeClass(row.sectionAGrade)}`}>
                                                            {row.sectionAGrade}
                                                        </span>
                                                    ) : (
                                                        <span className="text-gray-300">–</span>
                                                    )}
                                                </td>
                                            )}
                                            {visibleColumns.band && (
                                                <td className={`px-4 ${rowPaddingClass}`}>
                                                    {row.sectionBBand ? (
                                                        <div className="flex min-w-0 items-center gap-1.5">
                                                            <span className={`inline-block h-2 w-2 shrink-0 rounded-full ${getBandDotClass(row.sectionBBand)}`} />
                                                            <span className="max-w-40 truncate text-xs font-medium text-gray-800">{row.sectionBBand}</span>
                                                        </div>
                                                    ) : (
                                                        <span className="text-gray-300">–</span>
                                                    )}
                                                </td>
                                            )}
                                            {visibleColumns.assigned && (
                                                <td className={`whitespace-nowrap px-4 text-xs text-gray-500 ${rowPaddingClass}`}>
                                                    {formatDisplayDate(parseApiDate(row.assignedAt))}
                                                    {/* {row.assignedAt} */}
                                                </td>
                                            )}
                                            {visibleColumns.completed && (
                                                <td className={`whitespace-nowrap px-4 text-xs text-gray-500 ${rowPaddingClass}`}>
                                                    {formatDisplayDate(parseApiDate(row.completedTime))}
                                                </td>
                                            )}
                                            {visibleColumns.deadline && (
                                                <td className={`whitespace-nowrap px-4 text-xs text-gray-500 ${rowPaddingClass}`}>
                                                    {formatDisplayDate(parseApiDate(row.deadline))}
                                                </td>
                                            )}
                                            {visibleColumns.gateKeeperCount && (
                                                <td className={`whitespace-nowrap px-4 ${rowPaddingClass}`}>
                                                    {row.gateKeeperCount ?? "—"}
                                                </td>
                                            )}
                                            {visibleColumns.redFlagCount && (
                                                <td className={`whitespace-nowrap px-4 ${rowPaddingClass}`}>
                                                    {row.redFlagCount ?? "—"}
                                                </td>
                                            )}
                                            {visibleColumns.sectionAScore && (
                                                <td className={`whitespace-nowrap px-4 ${rowPaddingClass}`}>
                                                    {row.sectionAScore ?? "—"}
                                                </td>
                                            )}
                                            {visibleColumns.survivalScore && (
                                                <td className={`whitespace-nowrap px-4 ${rowPaddingClass}`}>
                                                    {row.survivalScore ?? "—"}
                                                </td>
                                            )}
                                            {visibleColumns.productivityScore && (
                                                <td className={`whitespace-nowrap px-4 ${rowPaddingClass}`}>
                                                    {row.productivityScore ?? "—"}
                                                </td>
                                            )}
                                            {visibleColumns.salesReadinessScore && (
                                                <td className={`whitespace-nowrap px-4 ${rowPaddingClass}`}>
                                                    {row.salesReadinessScore ?? "—"}
                                                </td>
                                            )}
                                            {visibleColumns.overallAssessmentScore && (
                                                <td className={`whitespace-nowrap px-4 ${rowPaddingClass}`}>
                                                    {row.overallAssessmentScore != null ? Number(row.overallAssessmentScore).toFixed(2) : "—"}
                                                </td>
                                            )}
                                            {visibleColumns.isEliminated && (
                                                <td className={`whitespace-nowrap px-4 ${rowPaddingClass}`}>
                                                    {row.isEliminated != null ? (row.isEliminated ? "Yes" : "No") : "—"}
                                                </td>
                                            )}
                                            <td className={`px-4 text-right ${rowPaddingClass}`}>
                                                <div className="flex items-center justify-end gap-1">
                                                    <button
                                                        type="button"
                                                        disabled={isNotStarted || downloadingRows.has(row.responderUID)}
                                                        onClick={() => handleRowDownload(row)}
                                                        title={isNotStarted ? "Download unavailable for not started assessments" : "Download report"}
                                                        className="grid h-8 w-8 place-items-center rounded-lg border border-[#7f35b2]/30 text-[#7f35b2] transition-colors hover:bg-[#c2a8f033] disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-400 disabled:hover:bg-transparent"
                                                    >
                                                        <Download size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="flex flex-col items-center gap-3 border-t border-gray-100 px-4 py-4 sm:flex-row sm:justify-between sm:px-6">
                    <span className="text-xs text-gray-500 sm:text-sm">
                        Showing {rangeStart} to {rangeEnd} of {filteredData.length} attempts
                    </span>
                    <div className="flex flex-col items-center gap-2 sm:flex-row">
                        <div className="flex flex-wrap items-center justify-center gap-1.5">
                            <button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(1)}
                                className="rounded-lg border border-gray-200 p-1.5 text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                                <ChevronsLeft size={16} />
                            </button>
                            <button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage((p) => p - 1)}
                                className="rounded-lg border border-gray-200 p-1.5 text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                                <ChevronLeft size={16} />
                            </button>
                            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#7f35b2] text-sm font-medium text-white">
                                {currentPage}
                            </span>
                            <button
                                disabled={currentPage === totalPages}
                                onClick={() => setCurrentPage((p) => p + 1)}
                                className="rounded-lg border border-gray-200 p-1.5 text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                                <ChevronRight size={16} />
                            </button>
                            <button
                                disabled={currentPage === totalPages}
                                onClick={() => setCurrentPage(totalPages)}
                                className="rounded-lg border border-gray-200 p-1.5 text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                                <ChevronsRight size={16} />
                            </button>
                        </div>
                        <select
                            value={pageSize}
                            onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                            className="rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm text-gray-600 focus:outline-none sm:ml-2"
                        >
                            {PAGE_SIZE_OPTIONS.map((n) => (
                                <option key={n} value={n}>{n} / page</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Reports;
